import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdmin } from '@/context/AdminContext';
import { useAdminTheme } from '@/context/AdminThemeContext';
import AdminSidebar from '@/components/admin/AdminSidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Key, Plus, Copy, Check, Trash2, RefreshCw, 
  Shield, Eye, EyeOff, AlertCircle, CheckCircle,
  Code, ExternalLink, Menu
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminApiKeys() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, authLoading } = useAdmin();
  const { colors } = useAdminTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDescription, setNewKeyDescription] = useState('');
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Check auth
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('adminToken');
    return { 'Authorization': `Bearer ${token}` };
  };

  const fetchApiKeys = async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/api-keys`, {
        headers: getAuthHeader()
      });
      
      if (response.status === 401) {
        // Don't redirect - let the auth context handle it
        toast.error('Session expirée. Veuillez vous reconnecter.');
        setLoading(false);
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch API keys');
      }
      
      const data = await response.json();
      setApiKeys(data.keys || []);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      toast.error('Erreur lors du chargement des clés API');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchApiKeys();
    }
  }, [authLoading, isAuthenticated]);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Le nom de la clé est requis');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/api-keys`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newKeyName,
          description: newKeyDescription,
          permissions: ['all']
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create API key');
      }

      const data = await response.json();
      setNewlyCreatedKey(data.key);
      setApiKeys(prev => [data, ...prev]);
      setNewKeyName('');
      setNewKeyDescription('');
      toast.success('Clé API créée avec succès');
    } catch (error) {
      console.error('Error creating API key:', error);
      toast.error('Erreur lors de la création de la clé API');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette clé API ?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });

      if (!response.ok) {
        throw new Error('Failed to delete API key');
      }

      setApiKeys(prev => prev.filter(k => k.id !== keyId));
      toast.success('Clé API supprimée');
    } catch (error) {
      console.error('Error deleting API key:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleKey = async (keyId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/api-keys/${keyId}/toggle`, {
        method: 'PUT',
        headers: getAuthHeader()
      });

      if (!response.ok) {
        throw new Error('Failed to toggle API key');
      }

      const data = await response.json();
      setApiKeys(prev => prev.map(k => 
        k.id === keyId ? { ...k, is_active: data.is_active } : k
      ));
      toast.success(data.is_active ? 'Clé activée' : 'Clé désactivée');
    } catch (error) {
      console.error('Error toggling API key:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success('Copié dans le presse-papiers');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Jamais';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className={`min-h-screen ${colors.bg} flex items-center justify-center`}>
        <RefreshCw className={`w-8 h-8 animate-spin ${colors.textSecondary}`} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${colors.bg} flex`}>
      <AdminSidebar 
        currentPath={location.pathname}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      
      <main className="flex-1 overflow-auto">
        {/* Mobile Header */}
        <div className={`lg:hidden sticky top-0 z-30 ${colors.bg} border-b ${colors.border} p-4`}>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            className={colors.text}
          >
            <Menu className="w-6 h-6" />
          </Button>
        </div>

        <div className="p-4 lg:p-8 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className={`text-2xl font-bold ${colors.text} flex items-center gap-2`}>
            <Key className="w-6 h-6 text-[#5BA946]" />
            Clés API
          </h1>
          <p className={colors.textSecondary}>
            Gérez les clés d'accès à l'API EdoMatch
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-[#5BA946] hover:bg-[#4a9339] text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle clé API
        </Button>
      </div>

      {/* API Documentation Link */}
      <div className={`${colors.card} rounded-xl p-4 border ${colors.border}`}>
        <div className="flex items-center gap-3">
          <Code className="w-5 h-5 text-blue-500" />
          <div className="flex-1">
            <p className={`font-medium ${colors.text}`}>Documentation API</p>
            <p className={`text-sm ${colors.textSecondary}`}>
              Base URL: <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">{API_URL}/api/v1</code>
            </p>
          </div>
          <a 
            href={`${API_URL}/docs`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-600 flex items-center gap-1"
          >
            Voir docs <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Newly Created Key Alert */}
      {newlyCreatedKey && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-green-800 dark:text-green-200">
                Clé API créée avec succès !
              </p>
              <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                Copiez cette clé maintenant. Elle ne sera plus affichée.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <code className="flex-1 bg-white dark:bg-gray-800 border border-green-200 dark:border-green-700 rounded px-3 py-2 font-mono text-sm break-all">
                  {newlyCreatedKey}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(newlyCreatedKey, 'new')}
                  className="shrink-0"
                >
                  {copiedId === 'new' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setNewlyCreatedKey(null)}
                className="mt-2 text-green-600"
              >
                J'ai copié la clé
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* API Keys List */}
      <div className={`${colors.card} rounded-xl border ${colors.border} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${colors.tableHeader} border-b ${colors.border}`}>
              <tr>
                <th className={`text-left p-4 ${colors.textSecondary} font-medium`}>Nom</th>
                <th className={`text-left p-4 ${colors.textSecondary} font-medium`}>Préfixe</th>
                <th className={`text-left p-4 ${colors.textSecondary} font-medium hidden sm:table-cell`}>Créée le</th>
                <th className={`text-left p-4 ${colors.textSecondary} font-medium hidden md:table-cell`}>Dernière utilisation</th>
                <th className={`text-left p-4 ${colors.textSecondary} font-medium`}>Statut</th>
                <th className={`text-right p-4 ${colors.textSecondary} font-medium`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="p-8 text-center">
                    <RefreshCw className={`w-6 h-6 animate-spin mx-auto ${colors.textSecondary}`} />
                  </td>
                </tr>
              ) : apiKeys.length === 0 ? (
                <tr>
                  <td colSpan="6" className={`p-8 text-center ${colors.textSecondary}`}>
                    <Key className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p>Aucune clé API</p>
                    <p className="text-sm">Créez votre première clé pour commencer</p>
                  </td>
                </tr>
              ) : (
                apiKeys.map((apiKey) => (
                  <tr key={apiKey.id} className={`border-b ${colors.border} hover:${colors.tableRowHover}`}>
                    <td className="p-4">
                      <div>
                        <p className={`font-medium ${colors.text}`}>{apiKey.name}</p>
                        {apiKey.description && (
                          <p className={`text-sm ${colors.textSecondary}`}>{apiKey.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <code className={`text-sm ${colors.textSecondary} bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded`}>
                        {apiKey.key_prefix}...
                      </code>
                    </td>
                    <td className={`p-4 ${colors.textSecondary} hidden sm:table-cell`}>
                      {formatDate(apiKey.created_at)}
                    </td>
                    <td className={`p-4 ${colors.textSecondary} hidden md:table-cell`}>
                      {formatDate(apiKey.last_used_at)}
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        apiKey.is_active 
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {apiKey.is_active ? (
                          <><CheckCircle className="w-3 h-3" /> Active</>
                        ) : (
                          <><AlertCircle className="w-3 h-3" /> Inactive</>
                        )}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleKey(apiKey.id)}
                          title={apiKey.is_active ? 'Désactiver' : 'Activer'}
                        >
                          {apiKey.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteKey(apiKey.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Usage Examples */}
      <div className={`${colors.card} rounded-xl border ${colors.border} p-6`}>
        <h3 className={`text-lg font-semibold ${colors.text} mb-4 flex items-center gap-2`}>
          <Code className="w-5 h-5" />
          Exemples d'utilisation
        </h3>
        <div className="space-y-4">
          <div>
            <p className={`text-sm font-medium ${colors.text} mb-2`}>Récupérer tous les profils :</p>
            <pre className={`bg-gray-100 dark:bg-gray-800 rounded-lg p-4 overflow-x-auto text-sm ${colors.textSecondary}`}>
{`curl -X GET "${API_URL}/api/v1/profiles" \\
  -H "X-API-Key: edo_votre_cle_api"`}
            </pre>
          </div>
          <div>
            <p className={`text-sm font-medium ${colors.text} mb-2`}>Récupérer un profil spécifique :</p>
            <pre className={`bg-gray-100 dark:bg-gray-800 rounded-lg p-4 overflow-x-auto text-sm ${colors.textSecondary}`}>
{`curl -X GET "${API_URL}/api/v1/profiles/{profile_id}" \\
  -H "X-API-Key: edo_votre_cle_api"`}
            </pre>
          </div>
          <div>
            <p className={`text-sm font-medium ${colors.text} mb-2`}>Télécharger un CV en PDF :</p>
            <pre className={`bg-gray-100 dark:bg-gray-800 rounded-lg p-4 overflow-x-auto text-sm ${colors.textSecondary}`}>
{`curl -X GET "${API_URL}/api/v1/profiles/{profile_id}/pdf" \\
  -H "X-API-Key: edo_votre_cle_api" \\
  -o cv.pdf`}
            </pre>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${colors.card} rounded-xl p-6 w-full max-w-md`}>
            <h2 className={`text-xl font-bold ${colors.text} mb-4 flex items-center gap-2`}>
              <Key className="w-5 h-5 text-[#5BA946]" />
              Nouvelle clé API
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${colors.text} mb-1`}>
                  Nom de la clé *
                </label>
                <Input
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="Ex: Integration App Mobile"
                  className={colors.input}
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${colors.text} mb-1`}>
                  Description (optionnel)
                </label>
                <Input
                  value={newKeyDescription}
                  onChange={(e) => setNewKeyDescription(e.target.value)}
                  placeholder="Ex: Clé pour l'application mobile EdoMatch"
                  className={colors.input}
                />
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                <div className="flex gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0" />
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    La clé API ne sera affichée qu'une seule fois après sa création. Assurez-vous de la copier et de la stocker en lieu sûr.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateModal(false);
                  setNewKeyName('');
                  setNewKeyDescription('');
                }}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleCreateKey}
                disabled={creating || !newKeyName.trim()}
                className="flex-1 bg-[#5BA946] hover:bg-[#4a9339] text-white"
              >
                {creating ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Création...</>
                ) : (
                  <><Plus className="w-4 h-4 mr-2" /> Créer la clé</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
        </div>
      </main>
    </div>
  );
}
